import { DetailsPanel } from '@components/detailsPanel/DetailsPanel';
import { PanelBackButton } from '@components/panel';
import { useStore } from '@store/GlobalStore';
import {
  AllPolicyDialogTypes,
  EndorsementPolicyDialogType,
  PolicyDialogType,
  PolicyTransactionStatus,
  PolicyTransactionTypes,
  RenewDialogType,
} from '@models/policyTransaction';
import { Box, Typography, ThemeProvider } from '@mui/material';
import { Panel } from '@components/panel/Panel';
import { getPolicyDetailsSections } from '@pages/PolicyDetails/PolicyDetails.constants';
import { UpdateBillPlanDialog } from '@pages/PolicyDetails/dialogs/UpdateBillPlanDialog';
import { CreatePolicyTransactionDialog } from '@pages/PolicyDetails/dialogs/CreatePolicyTransactionDialog';
import { ReviewAtRenewalModals } from '@pages/PolicyDetails/dialogs/ReviewAtRenewalModals';
import { ReviewAtRenewalActivityModal } from '@pages/PolicyDetails/dialogs/ReviewAtRenewalActivityModal';
import Loading from '@components/Loading';
import { useGetOoseResolution, useGetPolicy, useSearchPolicyTransactions } from '@services/policy';
import { Fragment, useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertStatus } from '@models/alert';
import { shallow } from 'zustand/shallow';
import { fakeLocalMidnightFromTZMidnight } from '@utils/date';
import { PolicyDetailsHeader } from '@pages/PolicyDetails/PolicyDetailsPanelHeader';
import composeTheme from '@theme/theme';
import { useSearchPolicyAttributes } from '@services/policy/policyAttributes';
import { useSearchPolicyTerms } from '@services/policyTerm/useSearchPolicyTerms';
import { CreateLinkedQuoteFromPolicyDialog } from '@pages/PolicyDetails/dialogs/CreateLinkedQuoteFromPolicyDialog/CreateLinkedQuoteFromPolicyDialog';
import { useSearchPaginatedBrokerageProducers } from '@services/brokerage/user/searchPaginatedBrokerageProducers';
import { MessagesDialog } from '@pages/PolicyDetails/dialogs/MessagesDialog/MessagesDialog';
import { NotesDialog } from '@components/notesDialog/NotesDialog';
import { PolicyDocumentArchiveDialog } from './PolicyDocumentArchiveTab';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { getContinuedToPolicy } from '@utils/policy/getContinuedToPolicy';
import { ExternalSentEmailsModal } from '@components/modals/ExternalSentEmailsModal/ExternalSentEmailsModal';
import { BORTransferDetailModal } from '@pages/PortalAdmin/BORTransferRequests/BORTransferDetailModal';
import { getRelatedContinuedQuotesBanner } from '@pages/PolicyDetails/helpers/getRelatedContinuedQuotesBanner';

interface Props {
  policyId?: string;
}

const getLastProcessedTransaction = (
  filteredItems: { effectiveOn: Date | null; processedOn: Date | null }[] | undefined,
) => {
  if (!filteredItems || filteredItems.length === 0) {
    return undefined;
  }
  const sortedItems = [...filteredItems].sort((a, b) => {
    const dateA = a.processedOn ? new Date(a.processedOn).getTime() : 0;
    const dateB = b.processedOn ? new Date(b.processedOn).getTime() : 0;
    return dateB - dateA;
  });

  return sortedItems[0];
};

export const PolicyDetailsPanel = ({ policyId }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;
  const [brokerageUserSearchbarText, setBrokerageUserSearchbarText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { policyTransactions } = useSearchPolicyTransactions({
    policyId: policyId ?? '',
    includeCanceled: false,
    pageLimit: 100,
  });

  const {
    policyTerms,
    isLoading: isPolicyTermsLoading,
    isError: isPolicyTermsError,
    refetch: refetchPolicyTerms,
  } = useSearchPolicyTerms({
    queryParams: { pageLimit: 100 },
    policyId: policyId ?? '',
  });

  const { policy, isLoading: isPolicyLoading } = useGetPolicy({ policyId });
  const { ooseResolution } = useGetOoseResolution({ policyId });
  const hasOoseResolutionInProgress = ooseResolution?.status === 'InProgress';
  const { policyAttributes, isLoading: isPolicyAttributesLoading } = useSearchPolicyAttributes({
    policyId,
    params: {
      quoteItemTypeCodeName: ['Policy'],
      quoteItemAttributeTypeCodeName: ['BillingMethod'],
    },
  });

  const { brokerageProducers, fetchNextPage: loadMoreBrokerageProducerResults } = useSearchPaginatedBrokerageProducers(
    {
      brokerageId: policy?.brokerage?.id,
      sort: '+user.firstName, +user.lastName',
      pageLimit: 25,
      searchText: brokerageUserSearchbarText,
    },
    { enabled: !!policy?.brokerage?.id },
  );

  const userAuth = useStore((state) => state.userAuth, shallow);
  const { isInternalUser, isCustomerUser } = userAuth;

  const [dialog, setDialog] = useState<{
    type: AllPolicyDialogTypes | undefined;
    show: boolean;
  }>({
    type: undefined,
    show: false,
  });

  // Open Messages dialog automatically when URL contains actions=messages, then strip it
  useEffect(() => {
    const actionsParam = searchParams.get('actions');
    if (!actionsParam) return;

    if (actionsParam === 'messages') {
      setDialog({ type: PolicyDialogType.Messages, show: true });
      searchParams.delete('actions');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const policyQuoteItemAttributes = policyAttributes?.quoteItems?.find(
    (qi: any) => qi.quoteItemType.codeName === 'Policy',
  );

  const billingMethodAttribute = policyQuoteItemAttributes?.quoteItemAttributes?.find(
    (qia: any) => qia.quoteItemAttributeType.codeName === 'BillingMethod',
  );

  const data = useMemo(() => {
    if (policy && !isPolicyAttributesLoading) {
      const policyDetailsSections = getPolicyDetailsSections(clockFormat);
      return policyDetailsSections?.map((section) => {
        return {
          ...section,
          rows: section.rows.map((row) => {
            return {
              fields: row.fields
                .map((el) => ({
                  ...el,
                  id: el.key(policy.id),
                  value: el.valueGetter({
                    policy,
                    fieldName: el.field,
                    policyTransactions: policyTransactions?.items,
                    isInternalUser,
                    attributes: {
                      billingMethod: (billingMethodAttribute?.value as string) ?? undefined,
                    },
                  }),
                }))
                .filter((item) => item.value !== undefined)
                .filter((item) => {
                  return item.isVisible?.({ isCustomerUser, isInternalUser }) ?? true;
                }),
            };
          }),
        };
      });
    }
    return [];
  }, [
    policy,
    policyTransactions?.items,
    isInternalUser,
    isCustomerUser,
    isPolicyAttributesLoading,
    billingMethodAttribute,
    clockFormat,
  ]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderNoPolicy = () => {
    return (
      <Panel>
        <Box sx={{ height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isPolicyLoading ? <Loading /> : <Typography variant='h5'>Policy not found</Typography>}
        </Box>
      </Panel>
    );
  };

  const handleHideDialog = () => {
    setDialog((prev) => ({ ...prev, show: false }));
  };

  const processedTransactions = useMemo(
    () => policyTransactions?.items?.filter((transaction) => transaction.status === 'Processed'),
    [policyTransactions],
  );

  const lastProcessedEffectiveDate = useMemo(() => {
    const lastProcessedEffectiveDate = getLastProcessedTransaction(processedTransactions)?.effectiveOn;
    if (lastProcessedEffectiveDate) {
      const date = new Date(lastProcessedEffectiveDate);
      return fakeLocalMidnightFromTZMidnight(date);
    }
    return null;
  }, [processedTransactions]);

  const lastProcessedCancelationEffectiveDate = useMemo(() => {
    const cancelationTransactions = processedTransactions?.filter(
      (transaction) => transaction.transactionType === PolicyTransactionTypes.Cancelation,
    );
    const lastCancelationEffectiveDate = getLastProcessedTransaction(cancelationTransactions)?.effectiveOn;
    if (lastCancelationEffectiveDate) {
      const date = new Date(lastCancelationEffectiveDate);
      return fakeLocalMidnightFromTZMidnight(date);
    }
    return null;
  }, [processedTransactions]);

  const sortedTransactions = useMemo(() => {
    return (policyTransactions?.items ?? []).sort(
      (
        a: { effectiveOn: Date | null; processedOn: Date | null },
        b: { effectiveOn: Date | null; processedOn: Date | null },
      ) => {
        const date1 = {
          a: a.effectiveOn && new Date(a.effectiveOn).getTime(),
          b: b.effectiveOn && new Date(b.effectiveOn).getTime(),
        };
        const date2 = {
          a: a.processedOn && new Date(a.processedOn).getTime(),
          b: b.processedOn && new Date(b.processedOn).getTime(),
        };

        return date1.a !== date1.b ? (date1.b ?? 0) - (date1.a ?? 0) : (date2.b ?? 0) - (date2.a ?? 0);
      },
    );
  }, [policyTransactions]);

  const selectedPolicyTransaction = useMemo(() => {
    switch (dialog.type) {
      case PolicyDialogType.Renew:
        return sortedTransactions?.find(
          (pt) =>
            pt.status === PolicyTransactionStatus.Created && pt.transactionType === PolicyTransactionTypes.Renewal,
        );
      case RenewDialogType.ConditionalRenewal:
        return sortedTransactions?.find(
          (pt) =>
            pt.status === PolicyTransactionStatus.Created &&
            pt.transactionType === PolicyTransactionTypes.ConditionalRenewal,
        );
      case EndorsementPolicyDialogType.Endorsement:
        return sortedTransactions?.find(
          (pt) =>
            pt.status === PolicyTransactionStatus.Created && pt.transactionType === PolicyTransactionTypes.Endorsement,
        );
      case PolicyDialogType.Rewrite:
        return sortedTransactions?.find(
          (pt) =>
            pt.status === PolicyTransactionStatus.Created &&
            pt.transactionType === PolicyTransactionTypes.NewBusinessRewrite,
        );
      case PolicyDialogType.Reinstatement:
        return sortedTransactions?.find(
          (pt) =>
            pt.status === PolicyTransactionStatus.Created &&
            pt.transactionType === PolicyTransactionTypes.Reinstatement,
        );

      case PolicyDialogType.Rescindment:
        return sortedTransactions?.find((el: any) => {
          return (
            el.status === PolicyTransactionStatus.Processed &&
            el.transactionType === PolicyTransactionTypes.Cancelation &&
            new Date(el.effectiveOn) > new Date()
          );
        });

      default:
        return undefined;
    }
  }, [dialog.type, sortedTransactions]);

  const createdRenewalTransaction = useMemo(
    () =>
      sortedTransactions?.find(
        (pt) => pt.status === PolicyTransactionStatus.Created && pt.transactionType === PolicyTransactionTypes.Renewal,
      ),
    [sortedTransactions],
  );

  const processedConditionalRenewalTransaction = useMemo(
    () =>
      sortedTransactions?.find(
        (pt) =>
          pt.status === PolicyTransactionStatus.Processed &&
          pt.transactionType === PolicyTransactionTypes.ConditionalRenewal,
      ),
    [sortedTransactions],
  );

  const showPolicyTransactionDialog =
    dialog.type &&
    ![
      PolicyDialogType.CreateLinkedPolicy,
      PolicyDialogType.Messages,
      PolicyDialogType.Notes,
      PolicyDialogType.MarkForReviewAtRenewal,
      PolicyDialogType.RemoveReviewAtRenewal,
      PolicyDialogType.ReviewAtRenewalActivity,
      PolicyDialogType.ExternalEmails,
      PolicyDialogType.ViewBORTransfer,
      PolicyDialogType.DocumentArchive,
      EndorsementPolicyDialogType.UpdateBillPlan,
      EndorsementPolicyDialogType.SequenceEndorsement,
    ].includes(dialog.type as any);

  const formattedPolicyNumber = policy
    ? getFormattedPolicyNumber(
        policy.policyNumberPrefix,
        policy.policyNumber.policyNumber,
        policy.activePolicyTerm?.termCount,
      )
    : '';

  const incompleteNamedInsuredUpdateRequest = policy?.customer?.incompleteNamedInsuredUpdateRequest ?? null;
  const namedInsuredEndorsementTransaction = incompleteNamedInsuredUpdateRequest?.endorsementTransaction ?? null;
  const endorsementQuoteId = namedInsuredEndorsementTransaction?.quoteId ?? undefined;

  const namedInsuredBanner =
    isInternalUser && namedInsuredEndorsementTransaction
      ? {
          text: 'This policy has a named insured endorsement that needs bound.',
          type: AlertStatus.Warning,
          ctaText: endorsementQuoteId ? 'Go to endorsement' : undefined,
          ctaFunction: endorsementQuoteId ? () => navigate(`/quote/${endorsementQuoteId}`) : undefined,
        }
      : undefined;

  const ooseBanner =
    hasOoseResolutionInProgress && policyId
      ? {
          text: 'An Out of Sequence Endorsement is in progress. No new transactions can be created until it is resolved or reverted.',
          type: AlertStatus.Warning,
          ctaText: 'Resume Resolution →',
          ctaUrl: `/policy/${policyId}/oose-resolution`,
        }
      : undefined;

  const continuedToPolicy = getContinuedToPolicy(policy?.relatedPolicyDestinations);
  const continuedToBanner = continuedToPolicy
    ? {
        text: (
          <>
            This policy has been continued with{' '}
            <Link to={`/policy/${continuedToPolicy.id}`}>
              {getFormattedPolicyNumber(
                continuedToPolicy.policyNumberPrefix,
                continuedToPolicy.policyNumber.policyNumber,
              )}
            </Link>
            .
          </>
        ),
        type: AlertStatus.Info,
      }
    : undefined;

  const relatedContinuedQuotesBanner = getRelatedContinuedQuotesBanner({
    isInternalUser,
    relatedContinuedQuotes: policy?.relatedContinuedQuotes,
  });

  const continuedQuotesBanner = relatedContinuedQuotesBanner
    ? {
        text: (
          <>
            {relatedContinuedQuotesBanner.quotes.length === 1
              ? 'This policy has a Convert to New Business continued quote: '
              : 'This policy has Convert to New Business continued quotes: '}
            {relatedContinuedQuotesBanner.quotes.map((quote, index) => (
              <Fragment key={quote.quoteId}>
                {index > 0 ? ', ' : null}
                <Link to={`/quote/${quote.quoteId}`}>{quote.displayQuoteNumber}</Link> ({quote.statusLabel})
              </Fragment>
            ))}
            .
          </>
        ),
        type: relatedContinuedQuotesBanner.type,
      }
    : undefined;

  return (
    <>
      {isCustomerUser ? (
        <PanelBackButton url='/?tab=myPolicies'>Back</PanelBackButton>
      ) : (
        <PanelBackButton url='/policies'>Back to Policies</PanelBackButton>
      )}

      {!policy && renderNoPolicy()}
      {policy && !isPolicyLoading && !isPolicyTermsLoading ? (
        <ThemeProvider theme={composeTheme('light')}>
          <Panel
            banner={[ooseBanner, namedInsuredBanner, continuedQuotesBanner, continuedToBanner].filter(
              (b): b is NonNullable<typeof b> => b !== undefined,
            )}
            sx={{
              borderLeftWidth: '8px',
              paddingBottom: '0',
            }}
          >
            <PolicyDetailsHeader
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              setDialog={setDialog}
              handleMenuClick={handleMenuClick}
              billingMethod={billingMethodAttribute?.value as string}
            />
            <DetailsPanel sections={data} dataTestId='policyDetails' />
          </Panel>
          {dialog.type === EndorsementPolicyDialogType.UpdateBillPlan ? (
            <UpdateBillPlanDialog
              policyTerms={policyTerms?.items ?? []}
              policyTransactions={policyTransactions?.items ?? []}
              handleHide={handleHideDialog}
              policy={policy}
              isOpen={dialog.show}
            />
          ) : null}
          {showPolicyTransactionDialog && !!policy ? (
            <CreatePolicyTransactionDialog
              isOpen={dialog.show ?? false}
              policy={policy}
              onCancel={() => {
                // calling setDialog with empty subverts the OOSE dialog, so let that one not get reset
                if (dialog.type !== EndorsementPolicyDialogType.Endorsement) {
                  setDialog({ type: undefined, show: false });
                }
                handleHideDialog();
              }}
              selectedPolicyTransaction={selectedPolicyTransaction}
              dialogType={dialog.type as any}
              policyTerms={policyTerms?.items ?? []}
              policyTermsError={isPolicyTermsError}
              onRetryPolicyTerms={() => void refetchPolicyTerms()}
              policyTransactions={policyTransactions?.items ?? []}
              other={{
                lastProcessedEffectiveDate: lastProcessedEffectiveDate ?? undefined,
                lastCancelationEffectiveDate: lastProcessedCancelationEffectiveDate ?? undefined,
                producerField: {
                  loadMore: loadMoreBrokerageProducerResults,
                  onDebouncedChange: (value: string) => {
                    setBrokerageUserSearchbarText(value);
                  },
                  brokerageProducers: brokerageProducers,
                },
                renewalQuoteId: createdRenewalTransaction?.quote?.id,
                conditionalRenewalTransactionId: processedConditionalRenewalTransaction?.id,
              }}
            />
          ) : null}
          {dialog.type === PolicyDialogType.CreateLinkedPolicy ? (
            <CreateLinkedQuoteFromPolicyDialog
              policyTransactions={policyTransactions?.items ?? []}
              policy={policy}
              onClose={handleHideDialog}
              isOpen={dialog.show}
            />
          ) : null}
          {dialog.type === PolicyDialogType.Messages && dialog.show ? (
            <MessagesDialog policyId={policy.id} onClose={handleHideDialog} isOpen={dialog.show} />
          ) : null}
          {dialog.type === PolicyDialogType.Notes && dialog.show ? (
            <NotesDialog
              open={dialog.show}
              onClose={handleHideDialog}
              title={formattedPolicyNumber ? `Policy #${formattedPolicyNumber} Notes` : 'Notes'}
              dialogContentProps={{
                policyId: policy.id,
                customerId: policy.customer.id,
                context: 'policy',
              }}
            />
          ) : null}
          {dialog.type === PolicyDialogType.DocumentArchive && dialog.show ? (
            <PolicyDocumentArchiveDialog
              open={dialog.show}
              onClose={handleHideDialog}
              title={formattedPolicyNumber ? `Policy #${formattedPolicyNumber} Documents` : 'Documents'}
            />
          ) : null}
          {dialog.type === PolicyDialogType.ExternalEmails && dialog.show ? (
            <ExternalSentEmailsModal open={dialog.show} onClose={handleHideDialog} context='policy' policy={policy} />
          ) : null}
          {(dialog.type === PolicyDialogType.MarkForReviewAtRenewal ||
            dialog.type === PolicyDialogType.RemoveReviewAtRenewal) &&
          dialog.show ? (
            <ReviewAtRenewalModals
              open={dialog.show}
              modalType={dialog.type === PolicyDialogType.MarkForReviewAtRenewal ? 'mark' : 'remove'}
              policy={policy}
              onClose={handleHideDialog}
            />
          ) : null}
          {dialog.type === PolicyDialogType.ReviewAtRenewalActivity && dialog.show ? (
            <ReviewAtRenewalActivityModal open={dialog.show} policy={policy} onClose={handleHideDialog} />
          ) : null}
          {dialog.type === PolicyDialogType.ViewBORTransfer && dialog.show && policy?.pendingBORTransferRequest && (
            <BORTransferDetailModal
              borTransferRequestId={policy.pendingBORTransferRequest.id}
              handleClose={handleHideDialog}
            />
          )}
        </ThemeProvider>
      ) : null}
    </>
  );
};
